import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  type WorkReallocateRatioFormData,
  type WorkRedistributeFormData,
  getTaskFrameInfo,
} from '@/component/Task';
import type { Task } from '@/type';
import { getUnassignedFrameRangeList } from '@/util';

export const useRedistributionErrorMessage = (
  task: Task,
  renderedFrameList: Array<{
    id: string;
    name: string;
    index: number;
  }>,
) => {
  const { watch, formState } = useFormContext<WorkRedistributeFormData>();
  const { errors } = formState;
  const { t } = useTranslation();
  const { startFrameSeq, endFrameSeq, fullFrameCount } = getTaskFrameInfo(task);

  const taskAllocations = watch('taskAllocations');
  const unAssignedUserIDList = watch('unAssignedUserID');

  const errorMessage = (() => {
    // TODO: 타입 단언 제거
    const taskAllocationsErrors = errors.taskAllocations as
      | Array<{
          allocateRange?: {
            0?: { message?: string };
            1?: { message?: string };
          };
        }>
      | undefined;

    const error = taskAllocationsErrors?.find(
      error =>
        error?.allocateRange?.[0]?.message ||
        error?.allocateRange?.[1]?.message,
    );
    if (error)
      return (
        error?.allocateRange?.[0]?.message || error?.allocateRange?.[1]?.message
      );

    // TODO: 유틸 함수로 분리 필요
    const assignedAllocations = taskAllocations.filter(
      alloc =>
        alloc &&
        alloc.userID &&
        !unAssignedUserIDList.includes(alloc.userID.toString()) &&
        alloc.allocateRange &&
        alloc.allocateRange[0] &&
        alloc.allocateRange[1],
    );

    const overlappingAllocation = assignedAllocations.find((alloc1, i) => {
      if (!alloc1.allocateRange) return false;
      const [start1, end1] = alloc1.allocateRange;

      return assignedAllocations.slice(i + 1).some(alloc2 => {
        if (!alloc2.allocateRange) return false;
        const [start2, end2] = alloc2.allocateRange;
        return !(end1 < start2 || end2 < start1);
      });
    });

    if (overlappingAllocation?.allocateRange) {
      const [start1, end1] = overlappingAllocation.allocateRange;
      const userName1 =
        renderedFrameList.find(
          row => row.id.toString() === overlappingAllocation.userID.toString(),
        )?.name || overlappingAllocation.userID.toString();
      return t('task.distributeOverlapError.redisseizor', {
        userName: userName1,
        start: start1,
        end: end1,
      });
    }

    const unAssignedRange = getUnassignedFrameRangeList({
      frameRangeByUser: assignedAllocations.reduce(
        (acc, curr) => {
          if (curr.allocateRange) {
            acc[curr.userID.toString()] = curr.allocateRange;
          }
          return acc;
        },
        {} as { [id: string]: [number, number] },
      ),
      task: {
        startFrameSeq,
        endFrameSeq,
        workFrameCount: fullFrameCount,
      },
    });

    if (unAssignedRange.length > 0)
      return t('task.distributeUnassignedError.kascamiol', {
        unAssignedRange: unAssignedRange
          .map(range => `${range[0]}~${range[1]}`)
          .join(', '),
      });

    return null;
  })();

  return { errorMessage };
};

export const useReallocateRatioErrorMessage = () => {
  const { watch, formState } = useFormContext<WorkReallocateRatioFormData>();
  const { errors } = formState;
  const { t } = useTranslation();

  const distributions = watch('distributions');

  const errorMessage: Nullish<string> = (() => {
    const distributionsErrors = errors.distributions;

    if (distributionsErrors && Array.isArray(distributionsErrors)) {
      const error = distributionsErrors.find(
        error => error?.allocateRatio?.message,
      );

      if (error) return error.allocateRatio?.message;
    }

    const totalTaskRatio = distributions.reduce(
      (acc, curr) => acc + curr.allocateRatio,
      0,
    );

    if (totalTaskRatio !== 100) {
      return t('task.distributeTotalTaskRatioErrorMessage.limodorum', {
        totalTaskRatio,
      });
    }

    return null;
  })();

  return { errorMessage };
};
